import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Instagram, Facebook, Music2, Linkedin, Youtube, ArrowUpRight, PhoneCall, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import Logo from "./Logo";
import CallbackModal from "./CallbackModal";
import { CrownMark } from "./CrownMark";
import { CrownWatermark } from "./CrownWatermark";
import { SERVICES } from "../data/content";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, EMAIL_MAILTO, TAGLINE } from "../data/contact";

const FootLink = ({ to, children, dim = false, end = false }) => (
  <NavLink to={to} end={end}
           className={({ isActive }) => `inline-block py-1.5 text-sm transition-colors ${dim ? "pl-3" : ""} ${
             isActive ? "nav-link-active text-gold" : dim ? "text-zinc-500 hover:text-neon" : "text-zinc-400 hover:text-neon"}`}>
    {children}
  </NavLink>
);

export default function Footer() {
  const [settings, setSettings] = useState(null);
  const [callback, setCallback] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  return (
    <footer className="relative overflow-hidden border-t border-white/10 bg-[#020202] noise pb-[calc(4rem+env(safe-area-inset-bottom))] lg:pb-0" data-testid="footer">
      <div className="relative mx-auto max-w-7xl px-5 md:px-10 py-20">
        <div className="relative mb-16">
          <CrownWatermark fit="h" opacity={0.12} sizes="(min-width: 1024px) 34vw, 60vw"
            className="hidden md:block right-[2%] -bottom-[4.35rem] h-[calc(100%+4rem)] lg:right-[6%]" testid="footer-crown-watermark" />
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold mb-4">From the Speedy family</p>
          <h2 className="font-heading font-black text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
            LET'S REBUILD<br /><span className="text-neon">YOUR COURT.</span>
          </h2>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/quote" data-testid="footer-quote-btn" className="rounded-sm bg-neon px-7 py-4 font-bold text-obsidian hover:bg-neon-hover transition-colors shadow-neon">
              Request a Quote
            </Link>
            <Link to="/court-scan" className="rounded-sm border border-white/20 px-7 py-4 font-bold text-white hover:border-neon hover:text-neon transition-colors">
              Try Speedy Court Scan™
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 border-t border-white/10 pt-14">
          <div className="col-span-2 md:col-span-1">
            <div className="mb-5 flex items-center gap-4" data-testid="footer-crown">
              <CrownMark className="h-12" />
              <span className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.3em] text-gold">{TAGLINE}</span>
            </div>
            <Logo size="footer" />
            <p className="mt-5 text-sm text-zinc-400 max-w-xs">
              A specialised division dedicated to transforming courts from the surface up. Built from the experience of Speedy Line Marking.
            </p>
            <a href={settings?.parent_url || "https://www.speedylinemarking.com.au"} target="_blank" rel="noreferrer"
               className="mt-4 inline-flex items-center gap-1 text-sm text-gold hover:text-gold-hover">
              Visit Speedy Line Marking <ArrowUpRight size={14} />
            </a>
          </div>

          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-4">Services</h4>
            <ul className="space-y-2.5">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <FootLink to={`/services/${s.slug}`}>{s.title}</FootLink>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-4">Explore</h4>
            <ul className="space-y-2.5">
              <li><FootLink to="/method" end>The Speedy Method</FootLink></li>
              <li><FootLink to="/method/consultation" dim>01 · Consultation</FootLink></li>
              <li><FootLink to="/method/preparation" dim>02 · Preparation</FootLink></li>
              <li><FootLink to="/method/execution" dim>03 · Execution</FootLink></li>
              <li><FootLink to="/method/final-inspection" dim>04 · Final Inspection</FootLink></li>
              <li><FootLink to="/blog-articles">Blog Articles</FootLink></li>
              <li><FootLink to="/sydney-court-resurfacing-guides">Sydney Court Guides</FootLink></li>
              <li><FootLink to="/sydney-court-resurfacing-portfolio">Sydney Court Portfolio</FootLink></li>
              <li><FootLink to="/assessment">Free Site Assessment</FootLink></li>
              <li><FootLink to="/compliance">Cm3 Compliance &amp; Safety</FootLink></li>
              <li><FootLink to="/gallery">The Court Archive</FootLink></li>
              <li><FootLink to="/customiser">3D Court Customiser</FootLink></li>
              <li><FootLink to="/court-scan">Speedy Court Scan™</FootLink></li>
              <li><FootLink to="/about">Team Speedy</FootLink></li>
              <li><Link to="/admin/login" className="inline-block py-1.5 text-sm text-zinc-600 hover:text-zinc-400">Staff Login</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-4">Contact</h4>
            <ul className="space-y-2.5 text-sm text-zinc-400">
              <li><a href={PHONE_TEL} className="hover:text-neon" data-testid="footer-phone">{PHONE_DISPLAY}</a></li>
              <li><a href={EMAIL_MAILTO} className="hover:text-neon break-all" data-testid="footer-email">{EMAIL}</a></li>
              <li>{settings?.address || "545 Merrylands Road, Merrylands NSW 2160"}</li>
              <li className="font-mono text-xs text-zinc-500 pt-1">Serving Sydney &amp; NSW · Sydney time</li>
            </ul>
            <button onClick={() => setCallback(true)} data-testid="footer-callback-btn"
                    className="mt-4 inline-flex items-center gap-2 rounded-sm border border-gold/40 px-4 py-2.5 text-sm font-bold text-gold hover:bg-gold hover:text-obsidian transition-colors">
              <PhoneCall size={15} /> Request a Callback
            </button>
            <div className="mt-5 flex gap-3">
              {[{ Icon: Instagram, k: "instagram" }, { Icon: Facebook, k: "facebook" }, { Icon: Music2, k: "tiktok" }, { Icon: Linkedin, k: "linkedin" }, { Icon: Youtube, k: "youtube" }].map(({ Icon, k }) => (
                <a key={k} href={settings?.[k] || "#"} target="_blank" rel="noreferrer"
                   className="flex h-9 w-9 items-center justify-center rounded-sm border border-white/10 text-zinc-400 hover:border-neon hover:text-neon transition-colors"
                   data-testid={`social-${k}`} title={`Speedy Resurfacing on ${k}`}>
                  <Icon size={16} />
                </a>
              ))}
            </div>
            <p className="mt-3 font-mono text-[10px] text-zinc-600">Follow Speedy Resurfacing on social media.</p>
          </div>
        </div>

        <div className="mt-14 flex flex-col md:flex-row justify-between gap-3 border-t border-white/10 pt-6 text-xs text-zinc-600">
          <p>
            © {new Date().getFullYear()} Speedy Resurfacing — a specialist division of Speedy Line Marking.
            <span className="mx-2 text-zinc-700">·</span>
            <Link to="/privacy-policy" data-testid="footer-privacy" className="hover:text-gold transition-colors">Privacy Policy</Link>
            <span className="mx-2 text-zinc-700">·</span>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-gold transition-colors">Terms of Use</Link>
          </p>
          <Link to="/compliance" data-testid="footer-cm3" className="inline-flex items-center gap-1.5 hover:text-gold transition-colors">
            <ShieldCheck size={12} className="text-gold" /> Cm3 Prequalified · ID {settings?.cm3_id || "87225"} · {settings?.public_liability || "$20,000,000"} Public Liability
          </Link>
        </div>
      </div>
      <CallbackModal open={callback} onClose={() => setCallback(false)} />
    </footer>
  );
}
